import { useLayoutEffect, MutableRef, useRef } from 'preact/hooks';

import { usePress } from '../hooks/UNSAFE_usePress';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useNavigationListContext } from './useNavigationListContext';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';

import {
  NavigationListItemStyles,
  NavigationListItemVariantOptions
} from './themes/NavigationListItemStyles.css';
import { NavigationListItemRedwoodTheme } from './themes/redwood/NavigationListItemTheme';

//Little hook to run some function before first render
export const useWillMount = (callback: () => void) => {
  const willMount = useRef(true);

  if (willMount.current) {
    willMount.current = false;
    callback();
  }
};

const flexItemStyleInterpolations = mergeInterpolations<FlexitemProps>([
  ...Object.values(flexitemInterpolations)
]);
const { class: labelContainerFlexItemClasses, ...labelContainerFlexItemStyle } =
  flexItemStyleInterpolations({
    alignSelf: 'center'
  });

// default style class for items in NavigationList
export const ITEM_STYLE_CLASS = 'oj-c-navigationlist-item';
export const ITEM_SELECTOR = '.' + ITEM_STYLE_CLASS;

export function useNavigationListItem<K extends string | number>({
  itemRef,
  itemKey
}: {
  itemRef: MutableRef<HTMLLIElement>;
  itemKey: K;
}) {
  const {
    onSelectionChange,
    selection,
    currentKey,
    showFocusRing,
    containsActiveElement,
    firstItemRenderedKey,
    onCurrentKeyChange
  } = useNavigationListContext();

  //Selection and focus state
  const isCurrent = currentKey === itemKey;

  useWillMount(() => {
    if (
      currentKey === undefined &&
      firstItemRenderedKey &&
      firstItemRenderedKey.current === undefined
    ) {
      firstItemRenderedKey.current = itemKey;
    }
  });

  const isSelected = selection === itemKey;

  //Styling
  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle, applyActiveStyle } =
    useInteractionStyle();

  const {
    classes: listItemClasses,
    styles: { labelContainerClasses }
  } = useComponentTheme<NavigationListItemVariantOptions, NavigationListItemStyles>(
    NavigationListItemRedwoodTheme,
    {
      focusRing: showFocusRing ? 'isFocusRing' : 'notFocusRing',
      current: isCurrent ? 'isCurrent' : 'notCurrtent',
      selected: isSelected ? 'isSelected' : 'notSelected',
      hybridHover: applyHoverStyle ? 'isHybridHover' : 'notHybridHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      active: applyActiveStyle ? 'isActive' : 'notActive'
    }
  );

  useLayoutEffect(() => {
    //We just focus the item if the container is the active element
    if (containsActiveElement?.() && isCurrent) {
      itemRef?.current?.focus();
    }
  }, [isCurrent, containsActiveElement]);

  //Item handlers
  const handlePress = () => {
    onSelectionChange?.({ value: itemKey as K });
  };

  //Legacy navlist makes selection on keyDown, but since preact tabbar made selection after keyUp, will do the same
  //for preact navlist
  const { pressProps } = usePress(handlePress);

  // We have to update current key if there no initial selection
  const onFocus = () => {
    if (currentKey === undefined) {
      onCurrentKeyChange({ value: itemKey as K });
    }
  };

  const onFocusProps = { onFocus };

  return {
    itemHandlers: mergeProps(interactionProps, pressProps, onFocusProps),
    itemClasses: classNames([listItemClasses, ITEM_STYLE_CLASS]),
    isItemFocusable:
      isCurrent || (firstItemRenderedKey?.current === itemKey && currentKey === undefined)
        ? true
        : false,
    isSelected,
    labelContainerClasses: classNames([labelContainerClasses, labelContainerFlexItemClasses]),
    labelContainerStyle: labelContainerFlexItemStyle
  };
}
